// CRM Market — the offering x segment evidence graph, three-column cockpit
// (25/50/25) mirroring the Intelligence idiom. Own namespace crmmkt-. Left rail:
// segment patterns (Intelligence view) or net-new prospects (Prospects view).
// Centre: market analyst chat, swaps to a dense pattern-detail surface. Right:
// recent signals feed. Never touches CrmIntelligence or its crmiq styles.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import API_BASE from '../../config';
import raptorIcon from '../../assets/raptor.svg';

const CONF = {
  strong:     { c: '#0F6E56', bg: '#E1F5EE', label: 'Strong' },
  moderate:   { c: '#854F0B', bg: '#FAEEDA', label: 'Moderate' },
  emerging:   { c: '#185FA5', bg: '#E6F1FB', label: 'Emerging' },
  hypothesis: { c: '#64748B', bg: '#EEF1F4', label: 'Hypothesis' },
};
const confOf = (k) => CONF[k] || CONF.hypothesis;
const bandColour = (k) => confOf(k).c;

function ukDate(v) { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
function relTime(v) { if (!v) return ''; const d = new Date(v); if (isNaN(d)) return ''; const diff = Date.now() - d.getTime(); const day = 86400000; if (diff < day) return 'Today'; if (diff < 2 * day) return 'Yesterday'; if (diff < 30 * day) return Math.floor(diff / day) + ' days ago'; return ukDate(v); }

const STARTERS = [
  'Which segments respond best right now?',
  'Where is my evidence still thin?',
  'What should I chase next and why?',
  'Which offering has the strongest validation?'
];

export default function CrmMarket() {
  const { clientId, client } = useOutletContext();

  const [view, setView] = useState('intelligence'); // intelligence | prospects
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [confFilter, setConfFilter] = useState('all');
  const [selected, setSelected] = useState(null); // selected pattern (offering_id)

  // One chat thread PER pattern (keyed by offering_id), plus '_general' for the
  // no-pattern analyst. Threads persist across navigation so a conversation can
  // always be resumed, like a normal chat app.
  const [threads, setThreads] = useState({});
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const threadKey = selected == null ? '_general' : String(selected);
  const chat = threads[threadKey] || [];
  const setChat = (updater) => setThreads(t => {
    const cur = t[threadKey] || [];
    const next = typeof updater === 'function' ? updater(cur) : updater;
    return { ...t, [threadKey]: next };
  });
  const chatEndRef = useRef(null);

  const [feed, setFeed] = useState([]);

  const [prospects, setProspects] = useState([]);
  const [prospectsLoading, setProspectsLoading] = useState(false);
  const [prospectPattern, setProspectPattern] = useState(null);

  const [drawerProspect, setDrawerProspect] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [addingCrm, setAddingCrm] = useState(false);
  const [addedCrm, setAddedCrm] = useState({});
  const [responding, setResponding] = useState([]);
  const [silent, setSilent] = useState([]);
  const [nodeTotals, setNodeTotals] = useState({ responding: 0, silent: 0, all: 0 });
  const [nodesLoading, setNodesLoading] = useState(false);
  const [nodeSel, setNodeSel] = useState(null);
  const [nodeQuery, setNodeQuery] = useState('');
  const [silentOffset, setSilentOffset] = useState(0);
  const SILENT_PAGE = 50;
  const loadNodes = useCallback(async (q, offset) => {
    setNodesLoading(true);
    try {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      qs.set('limit', String(SILENT_PAGE));
      qs.set('offset', String(offset || 0));
      const r = await fetch(`${API_BASE}/market/nodes?${qs}`);
      const d = await r.json();
      setResponding((d && d.responding) || []);
      setSilent((d && d.silent) || []);
      setNodeTotals((d && d.totals) || { responding: 0, silent: 0, all: 0 });
      setNodeSel(prev => prev || ((d && d.responding && d.responding[0]) || null));
    } catch (e) { setResponding([]); setSilent([]); }
    setNodesLoading(false);
  }, []);
  useEffect(() => {
    if (view !== 'nodes') return;
    const t = setTimeout(() => loadNodes(nodeQuery, silentOffset), nodeQuery ? 250 : 0);
    return () => clearTimeout(t);
  }, [view, nodeQuery, silentOffset, loadNodes]);


  async function addToCrm(pr) {
    if (!pr || addingCrm) return;
    setAddingCrm(true);
    try {
      const r = await fetch(`${API_BASE}/market/prospects/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, name: pr.name, website: pr.website, domain: pr.domain })
      });
      const d = await r.json();
      if (r.ok) setAddedCrm(m => ({ ...m, [pr.domain]: d.already ? 'exists' : 'added' }));
    } catch (e) { /* non-fatal */ }
    finally { setAddingCrm(false); }
  }

  function sendProspectToAnalyst(pr) {
    // Jump to the analyst on the pattern this prospect came from, and ask about it.
    setView('intelligence');
    if (prospectPattern) setSelected(prospectPattern.offering_id);
    setDrawerProspect(null);
    const q = `How should I approach ${pr.name} (${pr.domain}) for this offering?`;
    setTimeout(() => sendChat(q), 50);
  }

  async function openProspect(pr) {
    setDrawerProspect(pr);
    setDrawerData(null);
    setDrawerLoading(true);
    try {
      const r = await fetch(`${API_BASE}/market/prospects/enrich`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ website: pr.website, name: pr.name })
      });
      const d = await r.json();
      setDrawerData(r.ok ? d : { error: d.error || 'Enrichment failed' });
    } catch (e) { setDrawerData({ error: 'Network error' }); }
    finally { setDrawerLoading(false); }
  }

  async function findProspects(pattern) {
    if (!pattern) return;
    setView('prospects');
    setProspectPattern(pattern);
    setProspects([]);
    setProspectsLoading(true);
    try {
      const r = await fetch(`${API_BASE}/market/prospects`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, offeringId: pattern.offering_id, segment: pattern.segment, num_results: 20 })
      });
      const d = await r.json();
      setProspects(r.ok && Array.isArray(d.prospects) ? d.prospects : []);
    } catch (e) { setProspects([]); }
    finally { setProspectsLoading(false); }
  }

  const loadPatterns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/market/intelligence`);
      const d = await r.json();
      setPatterns(Array.isArray(d) ? d : []);
    } catch (e) { setPatterns([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadPatterns(); }, [loadPatterns]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat, chatBusy]);

  // On switching patterns: clear the composer, and load the persisted thread from
  // the server if we have not already loaded/started it this session.
  useEffect(() => {
    setChatInput('');
    const key = selected == null ? '_general' : String(selected);
    if (threads[key]) return; // already have it in memory
    const qs = selected == null ? 'offering_id=general' : `offering_id=${selected}`;
    fetch(`${API_BASE}/clients/${clientId}/market/analyst/thread?${qs}`)
      .then(r => r.ok ? r.json() : [])
      .then(rows => { if (Array.isArray(rows) && rows.length) setThreads(t => ({ ...t, [key]: rows })); })
      .catch(() => {});
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  async function sendChat(text) {
    const msg = (text != null ? text : chatInput).trim();
    if (!msg || chatBusy) return;
    const history = chat.map(m => ({ role: m.role, content: m.content }));
    // Anchor the analyst on the currently selected pattern so advice is specific
    // to THIS segment/offering, not generic. Keep the pattern selected so the
    // detail stays visible and the answer stacks beneath it.
    const focusPattern = patterns.find(p => p.offering_id === selected) || null;
    setChat(c => [...c, { role: 'user', content: msg }]);
    setChatInput('');
    setChatBusy(true);
    try {
      const r = await fetch(`${API_BASE}/market/analyst/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, history, focus: focusPattern, clientId, offeringId: selected })
      });
      const d = await r.json();
      setChat(c => [...c, { role: 'assistant', content: r.ok ? (d.answer || '') : ('That failed: ' + (d.error || 'unknown')) }]);
    } catch (e) {
      setChat(c => [...c, { role: 'assistant', content: 'Network error. Try again.' }]);
    } finally { setChatBusy(false); }
  }

  const filtered = patterns.filter(p => {
    if (confFilter !== 'all' && p.confidence !== confFilter) return false;
    if (search) {
      const hay = `${p.offering_label} ${p.statement}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });
  const selectedPattern = patterns.find(p => p.offering_id === selected) || null;

  // Module-level coverage counts across all patterns.
  const counts = {
    total: patterns.length,
    strong: patterns.filter(p => p.confidence === 'strong').length,
    moderate: patterns.filter(p => p.confidence === 'moderate').length,
    emerging: patterns.filter(p => p.confidence === 'emerging').length,
  };


  return (
    <div className="crmmkt3">

      {/* LEFT RAIL */}
      <div className="crmmkt3-rail">
        <div className="crmmkt3-rail-head">
          <div className="crmmkt3-toggle">
            <button className={'crmmkt3-tg' + (view === 'intelligence' ? ' crmmkt3-tg--on' : '')} onClick={() => setView('intelligence')}>Intelligence</button>
            <button className={'crmmkt3-tg' + (view === 'prospects' ? ' crmmkt3-tg--on' : '')} onClick={() => setView('prospects')}>Prospects</button>
            <button className={'crmmkt3-tg' + (view === 'nodes' ? ' crmmkt3-tg--on' : '')} onClick={() => setView('nodes')}>Nodes</button>
          </div>
          <input className="crmmkt3-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patterns…" />
          <div className="crmmkt3-filters">
            {['all', 'strong', 'moderate', 'emerging'].map(f => (
              <button key={f} className={'crmmkt3-fpill' + (confFilter === f ? ' crmmkt3-fpill--on' : '')} onClick={() => setConfFilter(f)}>
                {f === 'all' ? 'All' : confOf(f).label}
                {f !== 'all' && counts[f] > 0 && <span className="crmmkt3-fpill-n">{counts[f]}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="crmmkt3-list">
          {view === 'nodes' ? (
            nodesLoading ? <p className="crmmkt-empty">Loading…</p>
            : (responding.length === 0 && silent.length === 0) ? <p className="crmmkt-empty">No ingested companies yet. They appear here as LinkedHelper and email inbound resolve.</p>
            : (
              <>
                <input className="crmmkt3-nsearch" value={nodeQuery} onChange={e => { setSilentOffset(0); setNodeQuery(e.target.value); }} placeholder="Search companies…" />
                <div className="crmmkt3-prospect-head">{nodeTotals.all} in graph · {nodeTotals.responding} responding</div>
                {responding.map(c => { const ct = (c.contacts || [])[0]; return (
                  <div key={c.id} className={'crmmkt3-prospect crmmkt3-prospect--resp' + (nodeSel && nodeSel.id === c.id ? ' crmmkt3-prospect--on' : '')} onClick={() => setNodeSel(c)}>
                    <span className="crmmkt3-bandflex" />
                    <div className="crmmkt3-prospect-main">
                      <div className="crmmkt3-prospect-name">{c.name || c.domain}</div>
                      <div className="crmmkt3-prospect-dom">{c.domain}</div>
                      {ct && <div className="crmmkt3-prospect-sub">{[ct.full_name, ct.title].filter(Boolean).join(' · ')}</div>}
                    </div>
                    <div className="crmmkt3-prospect-right">
                      {c.latest_signal && <span className="crmmkt3-conf" style={{ color: '#0F6E56', background: '#E1F5EE' }}>{String(c.latest_signal.rung).replace(/_/g, ' ')}</span>}
                      {c.latest_signal && c.latest_signal.signal_at && <div className="crmmkt3-prospect-time">{relTime(c.latest_signal.signal_at)}</div>}
                    </div>
                  </div>
                ); })}
                {silent.length > 0 && <div className="crmmkt3-prospect-head">Contacted, no reply yet · {nodeTotals.silent}</div>}
                {silent.map(c => { const ct = (c.contacts || [])[0]; return (
                  <div key={c.id} className={'crmmkt3-prospect crmmkt3-prospect--silent' + (nodeSel && nodeSel.id === c.id ? ' crmmkt3-prospect--on' : '')} onClick={() => setNodeSel(c)}>
                    <div className="crmmkt3-prospect-main">
                      <div className="crmmkt3-prospect-name">{c.name || c.domain}</div>
                      <div className="crmmkt3-prospect-sub">{[ct && ct.full_name, ct && ct.title, c.updated_at && ('sent ' + relTime(c.updated_at))].filter(Boolean).join(' · ')}</div>
                    </div>
                    <svg className="crmmkt3-prospect-clock" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                  </div>
                ); })}
                {nodeTotals.silent > SILENT_PAGE && (
                  <div className="crmmkt3-pager">
                    <button className="crmmkt3-pager-btn" disabled={silentOffset === 0} onClick={() => setSilentOffset(Math.max(0, silentOffset - SILENT_PAGE))}>‹ Prev</button>
                    <span className="crmmkt3-pager-mid">{silentOffset + 1}–{Math.min(silentOffset + SILENT_PAGE, nodeTotals.silent)} of {nodeTotals.silent}</span>
                    <button className="crmmkt3-pager-btn" disabled={silentOffset + SILENT_PAGE >= nodeTotals.silent} onClick={() => setSilentOffset(silentOffset + SILENT_PAGE)}>Next ›</button>
                  </div>
                )}
              </>
            )
          ) : view === 'prospects' ? (
            prospectsLoading ? <p className="crmmkt-empty">Finding matching prospects…</p>
            : !prospectPattern ? (
              <div className="crmmkt-empty">
                <p>Net-new prospects appear here once you pick a pattern and run a match.</p>
                <p className="crmmkt-empty-sub">Select a pattern in Intelligence, then Find matching prospects.</p>
              </div>
            ) : prospects.length === 0 ? <p className="crmmkt-empty">No net-new prospects found for this pattern.</p>
            : (
              <>
                <div className="crmmkt3-prospect-head">{prospects.length} net-new prospects · {prospectPattern.offering_label}</div>
                {prospects.map((pr, i) => {
                  const cf = confOf(pr.confidence);
                  return (
                    <div key={i} className={'crmmkt3-prospect' + (drawerProspect && drawerProspect.domain === pr.domain ? ' crmmkt3-prospect--on' : '')} onClick={() => openProspect(pr)}>
                      <div className="crmmkt3-prospect-main">
                        <div className="crmmkt3-prospect-name">{pr.name}</div>
                        <div className="crmmkt3-prospect-dom">{pr.domain}</div>
                      </div>
                      {pr.evidence > 0 && <span className="crmmkt3-prospect-ev" style={{ color: cf.c, background: cf.bg }}>{pr.evidence}</span>}
                    </div>
                  );
                })}
              </>
            )
          ) : loading ? <p className="crmmkt-empty">Loading patterns…</p>
            : filtered.length === 0 ? <p className="crmmkt-empty">No validated patterns yet. Evidence accumulates as replies and deals come in.</p>
            : filtered.map(p => {
              const cf = confOf(p.confidence);
              const isSel = p.offering_id === selected;
              return (
                <div key={p.offering_id} className={'crmmkt3-row' + (isSel ? ' crmmkt3-row--on' : '')} onClick={() => setSelected(p.offering_id)}>
                  <span className="crmmkt3-band" style={{ background: cf.c }} />
                  <div className="crmmkt3-row-main">
                    <div className="crmmkt3-row-seg">{segLabel(p.segment)}</div>
                    <div className="crmmkt3-row-off">{p.offering_label}</div>
                  </div>
                  <span className="crmmkt3-conf" style={{ color: cf.c, background: cf.bg }}>{cf.label}</span>
                </div>
              );
            })}
        </div>
      </div>

      {/* CENTRE */}
      <div className="crmmkt3-centre">
        <div className="crmmkt3-centre-top">
          {selected != null && selectedPattern && (
            <div className="crmmkt3-surface">
              <button className="crmmkt3-back" onClick={() => setSelected(null)}>‹ Back to Raptor</button>
              <PatternDetail p={selectedPattern} onFindProspects={() => findProspects(selectedPattern)} />
              <div className="crmmkt3-surface-divider" />
            </div>
          )}
          <div className="crmmkt3-chat-body">
            {selected == null && chat.length === 0 && !chatBusy && (
              <div className="crmmkt3-welcome">
                <div className="crmmkt3-welcome-mark">◎</div>
                <p className="crmmkt3-welcome-lead">What does the market tell you?</p>
                <p className="crmmkt3-welcome-sub">{counts.total} validated patterns · {counts.strong} strong · {counts.emerging} emerging</p>
                <div className="crmmkt3-starters">
                  {STARTERS.map(s => <button key={s} className="crmmkt3-starter" onClick={() => sendChat(s)}>{s}</button>)}
                </div>
              </div>
            )}
            {selected != null && chat.length === 0 && !chatBusy && (
              <p className="crmmkt3-focus-hint">Ask Raptor about this pattern below. Advice will be specific to this segment and offering.</p>
            )}
            {chat.map((m, i) => (
              <div key={i} className={'crmmkt3-msg crmmkt3-msg--' + m.role}>
                {m.role === 'assistant' && <div className="crmmkt3-msg-mark">◎</div>}
                <div className="crmmkt3-msg-body">{m.role === 'assistant' ? <MarkdownLite text={m.content} /> : m.content}</div>
              </div>
            ))}
            {chatBusy && (
              <div className="crmmkt3-msg crmmkt3-msg--assistant">
                <div className="crmmkt3-msg-mark">◎</div>
                <div className="crmmkt3-msg-body crmmkt3-typing"><span></span><span></span><span></span></div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        </div>

        <div className="crmmkt3-composer-wrap">
          {selected != null && selectedPattern && (
            <div className="crmmkt3-composer-context">
              Asking about <strong>{selectedPattern.offering_label}</strong>
              <button className="crmmkt3-composer-clearctx" onClick={() => setSelected(null)} title="Clear">×</button>
            </div>
          )}
          <div className="crmmkt3-composer crmmkt3-composer--icon">
            <img className="crm-composer-raptor" src={raptorIcon} alt="Raptor" />
            <textarea className="crmmkt3-composer-ta" value={chatInput} onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
              placeholder="Ask Raptor…" rows={1} />
            <div className="crmmkt3-composer-bar">
              <span className="crmmkt3-composer-hint">Enter to send · Shift+Enter for newline</span>
              <button className="crmmkt3-composer-send" disabled={chatBusy || !chatInput.trim()} onClick={() => sendChat()} title="Send">
                <svg viewBox="0 0 16 16" width="15" height="15"><path d="M8 13V3M8 3L4 7M8 3l4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT FEED */}
      <div className="crmmkt3-feed">
        <div className="crmmkt3-feed-head"><div className="crmmkt3-feed-title">Recent signals</div></div>
        <div className="crmmkt3-feed-body">
          {feed.length === 0
            ? <p className="crmmkt-empty">Signals from replies and won deals will appear here.</p>
            : feed.map((s, i) => (
              <div key={i} className="crmmkt3-feed-item">
                <span className="crmmkt3-feed-dot" style={{ background: s.weight >= 0 ? '#0F6E56' : '#993C1D' }} />
                <div>
                  <div className="crmmkt3-feed-line">{s.rung} · {s.sector || 'unknown'}</div>
                  <div className="crmmkt3-feed-time">{relTime(s.signal_at)}</div>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* PROSPECT DRAWER */}
      {view === 'nodes' && nodeSel && (
        <>
          <div className="crmmkt-drawer-scrim" onClick={() => setNodeSel(null)} />
          <div className="crmmkt-drawer">
            <div className="crmmkt-drawer-head">
              <div>
                <div className="crmmkt-drawer-name">{nodeSel.name || nodeSel.domain}</div>
                <span className="crmmkt-drawer-dom">{nodeSel.domain}</span>
              </div>
              <button className="crmmkt-drawer-x" onClick={() => setNodeSel(null)}>×</button>
            </div>

            {nodeSel.latest_signal && (
              <div className="crmmkt-drawer-ev">
                <span className="crmmkt-conf-lg" style={{ color: '#0F6E56', background: '#E1F5EE' }}>{String(nodeSel.latest_signal.rung).replace(/_/g, ' ')}</span>
                <span className="crmmkt-drawer-evnum">{nodeSel.signal_count} signal{nodeSel.signal_count === 1 ? '' : 's'}</span>
              </div>
            )}

            <div className="crmmkt-drawer-body">
              {nodeSel.description && (
                <div className="crmmkt-drawer-sec">
                  <div className="crmmkt-drawer-h">What they do</div>
                  <p className="crmmkt-drawer-txt">{nodeSel.description}</p>
                </div>
              )}

              {(nodeSel.contacts || []).length === 0 ? (
                <div className="crmmkt-drawer-sec">
                  <div className="crmmkt-drawer-h">Contacts</div>
                  <p className="crmmkt-drawer-txt crmmkt-drawer-txt--muted">No contact resolved yet. This company entered the graph from a domain match.</p>
                </div>
              ) : (nodeSel.contacts || []).map(ct => {
                const tags = (ct.tags || '').split(',').map(t => t.trim()).filter(Boolean);
                const emp = Array.isArray(ct.employment) ? ct.employment : [];
                const thread = Array.isArray(ct.thread) ? ct.thread : [];
                return (
                  <div key={ct.id}>
                    <div className="crmmkt-drawer-sec">
                      <div className="crmmkt-drawer-h">Contact</div>
                      <div className="crmmkt-cperson">
                        <div className="crmmkt-cavatar">{(ct.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}</div>
                        <div>
                          <div className="crmmkt-cname">{ct.full_name}</div>
                          <div className="crmmkt-ctitle">{ct.title}</div>
                        </div>
                      </div>
                      <p className="crmmkt-drawer-txt crmmkt-drawer-txt--muted">
                        {[ct.industry, ct.campaign_name && ('via ' + ct.campaign_name), ct.mutual_count && (ct.mutual_count + ' mutual'), ct.connections_count && (ct.connections_count + ' connections'), ct.email].filter(Boolean).join(' · ')}
                      </p>
                      {tags.length > 0 && <div className="crmmkt-ctags">{tags.map((t, i) => <span key={i} className={'crmmkt-ctag crmmkt-ctag--' + (i % 4)}>{t}</span>)}</div>}
                      {ct.linkedin_url && <p className="crmmkt-drawer-txt"><a href={ct.linkedin_url} target="_blank" rel="noreferrer">LinkedIn profile ↗</a></p>}
                    </div>

                    {emp.length > 0 && (
                      <div className="crmmkt-drawer-sec">
                        <div className="crmmkt-drawer-h">Career</div>
                        {emp.map((e, i) => (
                          <p key={i} className={'crmmkt-drawer-txt' + (e.is_current ? '' : ' crmmkt-drawer-txt--muted')}>
                            {e.title || '—'} · {e.name}{e.is_current ? ', now' : ''}{e.start ? ' (from ' + e.start + ')' : ''}
                          </p>
                        ))}
                      </div>
                    )}

                    {thread.length > 0 && (
                      <div className="crmmkt-drawer-sec">
                        <div className="crmmkt-drawer-h">Conversation</div>
                        <div className="crmmkt-chat">
                          {thread.map((m, i) => (
                            <div key={i} className={'crmmkt-chat-row crmmkt-chat-row--' + m.dir}>
                              <div className={'crmmkt-chat-bub crmmkt-chat-bub--' + m.dir}>
                                {m.text}
                                {m.at && <span className="crmmkt-chat-at">{ukDate(m.at)}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {nodeSel.latest_signal && (
                <div className="crmmkt-drawer-sec">
                  <div className="crmmkt-drawer-h">Latest signal</div>
                  <p className="crmmkt-drawer-txt"><strong>{String(nodeSel.latest_signal.rung).replace(/_/g, ' ')}</strong>{nodeSel.latest_signal.seniority ? ' · ' + nodeSel.latest_signal.seniority : ''}</p>
                  {nodeSel.latest_signal.reply_text && <p className="crmmkt-drawer-txt crmmkt-drawer-txt--muted">{nodeSel.latest_signal.reply_text}</p>}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {drawerProspect && (
        <>
          <div className="crmmkt-drawer-scrim" onClick={() => setDrawerProspect(null)} />
          <div className="crmmkt-drawer">
            <div className="crmmkt-drawer-head">
              <div>
                <div className="crmmkt-drawer-name">{drawerProspect.name}</div>
                <a className="crmmkt-drawer-dom" href={drawerProspect.website} target="_blank" rel="noreferrer">{drawerProspect.domain} ↗</a>
              </div>
              <button className="crmmkt-drawer-x" onClick={() => setDrawerProspect(null)}>×</button>
            </div>

            {drawerProspect.evidence > 0 && (
              <div className="crmmkt-drawer-ev">
                <span className="crmmkt-conf-lg" style={{ color: confOf(drawerProspect.confidence).c, background: confOf(drawerProspect.confidence).bg }}>{confOf(drawerProspect.confidence).label}</span>
                <span className="crmmkt-drawer-evnum">Evidence {drawerProspect.evidence}</span>
              </div>
            )}

            <div className="crmmkt-drawer-body">
              {drawerLoading ? <p className="crmmkt-empty">Pulling company intelligence…</p>
                : drawerData?.error ? <p className="crmmkt-empty">{drawerData.error}</p>
                : drawerData ? (
                  <>
                    {drawerData.summary && (
                      <div className="crmmkt-drawer-sec">
                        <div className="crmmkt-drawer-h">What they do</div>
                        <p className="crmmkt-drawer-txt">{drawerData.summary}</p>
                      </div>
                    )}
                    {prospectPattern && (
                      <div className="crmmkt-drawer-sec">
                        <div className="crmmkt-drawer-h">Why it matches</div>
                        <p className="crmmkt-drawer-txt">Matches your validated pattern for <strong>{prospectPattern.offering_label}</strong>: {segLabel(prospectPattern.segment)}.</p>
                      </div>
                    )}
                    {drawerData.text && (
                      <div className="crmmkt-drawer-sec">
                        <div className="crmmkt-drawer-h">From their site</div>
                        <div className="crmmkt-drawer-txt crmmkt-drawer-txt--muted"><MarkdownLite text={drawerData.text} /></div>
                      </div>
                    )}
                  </>
                ) : null}
            </div>

            <div className="crmmkt-drawer-actions">
              {addedCrm[drawerProspect.domain] === 'added' ? (
                <button className="crmmkt-btn-added" disabled>✓ Added to CRM</button>
              ) : addedCrm[drawerProspect.domain] === 'exists' ? (
                <button className="crmmkt-btn-added" disabled>Already in CRM</button>
              ) : (
                <button className="crmmkt-btn-primary" disabled={addingCrm} onClick={() => addToCrm(drawerProspect)}>{addingCrm ? 'Adding…' : 'Add to CRM'}</button>
              )}
              <button className="crmmkt-btn-ghost" onClick={() => sendProspectToAnalyst(drawerProspect)}>Ask Raptor about this company</button>
            </div>
          </div>
        </>
      )}

    </div>
  );
}

// Build a compact segment label from the dominant-attributes object.
function segLabel(seg) {
  if (!seg) return 'Segment';
  const bits = [];
  if (seg.sector) bits.push(seg.sector.value);
  if (seg.employee_band) bits.push(seg.employee_band.value);
  if (seg.city && seg.city.share >= 60) bits.push(seg.city.value);
  else if (seg.region) bits.push(seg.region.value);
  return bits.join(' · ') || 'Segment';
}

// Human masked sentence from a pattern row (mirrors backend toSentence).
function statementOf(p) {
  return p.statement || `${segLabel(p.segment)} respond well to ${p.offering_label}`;
}

// Dense pattern detail: the full evidence breakdown, not just a number.
function PatternDetail({ p, onFindProspects }) {
  const cf = confOf(p.confidence);
  const seg = p.segment || {};
  const dims = [
    ['Sector', seg.sector], ['Size', seg.employee_band], ['Region', seg.region],
    ['City', seg.city], ['Revenue', seg.revenue_band], ['Model', seg.business_model],
    ['Stack', seg.tech_stack]
  ].filter(([, v]) => v);

  return (
    <div className="crmmkt-detail">
      <div className="crmmkt-detail-statement">{statementOf(p)}</div>

      <div className="crmmkt-detail-meta">
        <span className="crmmkt-conf-lg" style={{ color: cf.c, background: cf.bg }}>{cf.label}</span>
        <span className="crmmkt-metric"><b>{p.evidence}</b><i>evidence</i></span>
        <span className="crmmkt-metric"><b>{p.campaign_count || 1}</b><i>campaigns</i></span>
        <span className="crmmkt-metric"><b>{p.distinct_days || 1}</b><i>active days</i></span>
        <span className="crmmkt-metric"><b>{p.positive_count || 0}/{p.signal_count || 0}</b><i>positive</i></span>
      </div>

      {/* positive vs negative bar */}
      <div className="crmmkt-split">
        <div className="crmmkt-split-bar">
          <span className="crmmkt-split-pos" style={{ flex: Math.max(p.positive || 0, 0.01) }} />
          <span className="crmmkt-split-neg" style={{ flex: Math.max(Math.abs(p.negative || 0), 0.01) }} />
        </div>
        <div className="crmmkt-split-legend">
          <span><i className="crmmkt-dot-pos" /> +{p.positive || 0} positive</span>
          <span><i className="crmmkt-dot-neg" /> {p.negative || 0} negative</span>
        </div>
      </div>

      <div className="crmmkt-detail-h">Dominant attributes</div>
      <div className="crmmkt-dims">
        {dims.length === 0 ? <span className="crmmkt-empty-sm">Attributes fill in as responded companies are enriched.</span>
          : dims.map(([label, v]) => (
            <span key={label} className="crmmkt-dim">
              {label}: <b>{Array.isArray(v.value) ? v.value.join(', ') : v.value}</b> <i>{v.share}%</i>
            </span>
          ))}
      </div>

      {p.group_label && <div className="crmmkt-detail-group">Part of <b>{p.group_label}</b></div>}

      <div className="crmmkt-detail-actions">
        <button className="crmmkt-btn-primary" onClick={onFindProspects}>Find matching prospects →</button>
      </div>
      <p className="crmmkt-detail-note">Net-new companies matching this pattern, ranked by evidence. Companies inside another client's private CRM are never shown.</p>
    </div>
  );
}

// Lightweight markdown renderer for analyst answers: bold, bullets, numbered
// lists, headings, links, paragraph spacing. Keeps raw asterisks out of the UI.
function renderInline(text, keyBase) {
  // links [label](url) first, then **bold**, then inline `code`
  const nodes = [];
  let rest = text;
  let idx = 0;
  const linkRe = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/;
  while (true) {
    const m = rest.match(linkRe);
    if (!m) break;
    const before = rest.slice(0, m.index);
    if (before) nodes.push(before);
    nodes.push(<a key={keyBase + '-a' + (idx++)} href={m[2]} target="_blank" rel="noreferrer" className="crmmkt-md-link">{m[1]}</a>);
    rest = rest.slice(m.index + m[0].length);
  }
  if (rest) nodes.push(rest);
  // bold pass over string nodes
  const out = [];
  nodes.forEach((n, i) => {
    if (typeof n !== 'string') { out.push(n); return; }
    const parts = n.split(/\*\*([^*]+)\*\*/g);
    parts.forEach((p, j) => {
      if (j % 2 === 1) out.push(<strong key={keyBase + '-b' + i + '-' + j}>{p}</strong>);
      else if (p) out.push(<span key={keyBase + '-s' + i + '-' + j}>{p}</span>);
    });
  });
  return out;
}

function MarkdownLite({ text }) {
  if (!text) return null;
  const lines = String(text).replace(/\r/g, '').split('\n');
  const blocks = [];
  let list = null; let listType = null;

  const flush = () => { if (list) { blocks.push(listType === 'ol'
    ? <ol key={'l' + blocks.length} className="crmmkt-md-ol">{list}</ol>
    : <ul key={'l' + blocks.length} className="crmmkt-md-ul">{list}</ul>); list = null; listType = null; } };

  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const heading = line.match(/^#{1,4}\s+(.*)$/);
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const num = line.match(/^\s*\d+\.\s+(.*)$/);
    if (heading) { flush(); blocks.push(<div key={'h' + i} className="crmmkt-md-h">{renderInline(heading[1], 'h' + i)}</div>); return; }
    if (bullet) { if (listType !== 'ul') flush(); listType = 'ul'; list = list || []; list.push(<li key={'li' + i}>{renderInline(bullet[1], 'li' + i)}</li>); return; }
    if (num) { if (listType !== 'ol') flush(); listType = 'ol'; list = list || []; list.push(<li key={'li' + i}>{renderInline(num[1], 'li' + i)}</li>); return; }
    if (line === '') { flush(); blocks.push(<div key={'sp' + i} className="crmmkt-md-sp" />); return; }
    flush(); blocks.push(<p key={'p' + i} className="crmmkt-md-p">{renderInline(line, 'p' + i)}</p>);
  });
  flush();
  return <div className="crmmkt-md">{blocks}</div>;
}
